import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useParams, Link, useLocation, useNavigate } from 'react-router-dom';
import vendaService, { VendaResponseDTO } from '../services/vendaService';
import paymentService from '../services/paymentService';
import { PaymentResponseDTO } from '../types/payment';
import printService from '../services/printService';
import { authService } from '../services/authService';

function formatCurrency(v?: number) {
  return (v ?? 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}
function formatDateTime(iso?: string) {
  if (!iso) return '';
  const d = new Date(iso);
  const data = d.toLocaleDateString('pt-BR');
  const hora = d.toLocaleTimeString('pt-BR');
  return `${data} ${hora}`;
}

const CupomPrintPage: React.FC = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [venda, setVenda] = useState<VendaResponseDTO | null>(null);
  const [payments, setPayments] = useState<PaymentResponseDTO[]>([]);
  const printedRef = useRef(false);
  const shouldAutoPrint = useMemo(() => {
    const q = new URLSearchParams(location.search);
    return q.get('print') === '1';
  }, [location.search]);
  const [preferredPrinter, setPreferredPrinter] = useState<string>('Padrão do sistema');
  const [printHint, setPrintHint] = useState<string>('');
  useEffect(() => {
    // Lê preferência do storage e tenta obter impressora padrão via QZ Tray
    (async () => {
      try {
        const raw = localStorage.getItem('app-settings');
        if (raw) {
          const s = JSON.parse(raw);
          if (s?.printerMode === 'manual' && s?.printerName) {
            setPreferredPrinter(String(s.printerName));
          }
        }
        // Se QZ estiver disponível, mostrar impressora padrão real
        const defaultPrinter = await printService.getDefaultPrinter();
        if (defaultPrinter) setPreferredPrinter(defaultPrinter);
        const available = await printService.isQZAvailable();
        setPrintHint(available ? 'Impressão direta habilitada (QZ Tray).' : 'Abrirá diálogo do navegador.');
      } catch {
        setPrintHint('Abrirá diálogo do navegador.');
      }
    })();
  }, []);

  // Atalho F6 para voltar ao PDV
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.key || '') === 'F6') {
        e.preventDefault();
        navigate('/pdv');
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [navigate]);

  // Normaliza itens (lida com variações de payload do backend)
  const itens = useMemo(() => {
    const raw = (venda?.itens ?? []) as any[];
    return raw.map((it: any) => {
      const quantidade = Number(it?.quantidade ?? it?.qtd ?? 0);
      const precoUnitario = Number(it?.precoUnitario ?? it?.preco ?? it?.valorUnitario ?? 0);
      const valorTotal = Number(it?.valorTotal ?? it?.total ?? it?.valor ?? (precoUnitario * quantidade));
      const produtoId = String(it?.produtoId ?? it?.produto?.id ?? '');
      const produtoNome = it?.produtoNome ?? it?.produto?.nome ?? '';
      const produtoCodigoBarras = it?.produtoCodigoBarras ?? it?.produto?.codigoBarras ?? it?.codigoBarras ?? '';
      const medSigla = it?.embalagemSigla ?? it?.unidade ?? 'UN';
      return { produtoId, produtoNome, produtoCodigoBarras, quantidade, precoUnitario, valorTotal, medSigla };
    });
  }, [venda]);

  useEffect(() => {
    const load = async () => {
      if (!id) { setError('Venda não identificada'); return; }
      setLoading(true);
      setError('');
      try {
        const vRes = await vendaService.obter(id);
        // Compatível com respostas do backend que usam envelope { success, message, data }
        const vendaPayload: any = (vRes as any)?.data?.data ?? (vRes as any)?.data;
        setVenda(vendaPayload as VendaResponseDTO);

        const pRes = await paymentService.listBySalePath(id);
        const pList = ((pRes as any)?.data?.data?.content ?? (pRes as any)?.data?.content ?? (pRes as any)?.data ?? []) as PaymentResponseDTO[];
        setPayments(pList);
      } catch (e: any) {
        setError(e?.message || 'Falha ao carregar dados da venda');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  const subtotal = useMemo(() => itens.reduce((s, it) => s + (Number(it.valorTotal) || 0), 0), [itens]);

  const pagos = useMemo(() => payments.filter(p => ['EFETUADO', 'PAGO'].includes(String(p.status).toUpperCase())), [payments]);
  const totalPago = useMemo(() => pagos.reduce((s, p) => s + (p.valor || 0), 0), [pagos]);

  const total = useMemo(() => {
    const vt = Number(venda?.valorTotal ?? 0);
    if (vt > 0) return vt;
    if (subtotal > 0) return subtotal;
    // Fallback: se não há itens/total retornados, usar o somatório dos pagamentos
    if (totalPago > 0) return totalPago;
    return 0;
  }, [venda, subtotal, totalPago]);

  const troco = useMemo(() => Math.max(0, totalPago - total), [totalPago, total]);

  const handlePrint = async () => {
    try {
      await printService.trySilentOrPreview({ title: 'Cupom de Venda', deviceName: preferredPrinter });
    } catch (e) {
      // Se algo falhar, ainda assim tentar preview
      window.print();
    }
  };

  // Dispara impressão automática quando solicitado por parâmetro de rota
  useEffect(() => {
    if (printedRef.current) return;
    if (!shouldAutoPrint) return;
    if (loading) return;
    if (error) return;
    // Garante que há conteúdo (venda carregada) antes de imprimir
    if (!venda) return;
    printedRef.current = true;
    // Pequeno atraso para garantir renderização concluída
    const t = setTimeout(() => { printService.trySilentOrPreview({ title: 'Cupom de Venda', deviceName: preferredPrinter }); }, 300);
    return () => clearTimeout(t);
  }, [shouldAutoPrint, loading, error, venda]);

  return (
    <div className="min-h-screen bg-gray-100 p-4">
      <div className="max-w-md mx-auto bg-white shadow rounded p-0 text-black cupom-root font-bold">
          <div className="flex items-center justify-between mb-3 print:hidden">
            <h1 className="text-lg font-bold">Cupom de Venda</h1>
            <div className="space-x-2">
              <button onClick={handlePrint} className="px-3 py-1.5 bg-red-600 text-white rounded hover:bg-blue-700">Imprimir</button>
            <Link to="/pdv" title="F6" className="px-3 py-1.5 bg-gray-200 text-gray-800 rounded hover:bg-gray-300">Voltar ao PDV (F6)</Link>
            </div>
          </div>
        <div className="text-xs text-gray-500 mb-2 print:hidden">Impressora preferida: {preferredPrinter} <span className="ml-2 text-gray-400">{printHint}</span></div>

        {error && <div className="text-red-600 text-sm mb-2">{error}</div>}
        {loading && <div className="text-gray-600 text-sm mb-2">Carregando...</div>}

        <div className="text-center">
          <p className="font-bold">{authService.getCompanyName() || 'COMERCIAL'}</p>
          <p className="text-sm">Documento não fiscal</p>
        </div>

        <div className="border-t border-b py-0 text-sm mb-0">
          <div className="flex justify-between">
            <span>Cupom</span>
            <span>{venda?.id}</span>
          </div>
          <div className="flex justify-between">
            <span>Cliente</span>
            <span>{venda?.nomeCliente ?? 'Consumidor Final'}</span>
          </div>
          <div className="flex justify-between">
            <span>Emitido em</span>
            <span>{formatDateTime(venda?.dataCriacao)}</span>
          </div>
          <div className="flex justify-between">
            <span>Vendido por</span>
            <span>{venda?.vendedorNome ?? '-'}</span>
          </div>
        </div>

        {/* Separador entre dados do cliente e itens */}
        <div className="w-full overflow-hidden whitespace-nowrap text-[11px] font-mono select-none my-0">{'='.repeat(256)}</div>

        <div className="mt-0">
          <div className="flex text-xs font-bold border-b pb-1">
            <div className="w-12">COD</div>
            <div className="flex-1">PRODUTO</div>
            <div className="w-10 text-right">QTD</div>
            <div className="w-10 text-center">MED</div>
            <div className="w-16 text-right">VALOR</div>
            <div className="w-16 text-right">TOTAL</div>
          </div>
          {/* Separador logo abaixo do header dos itens */}
          <div className="w-full overflow-hidden whitespace-nowrap text-[11px] font-mono select-none my-0">{'='.repeat(256)}</div>
          <div className="text-xs">
            {itens.map((it, idx) => (
              <div key={idx} className="flex py-1 border-b">
                <div className="w-12">{idx + 1}</div>
                <div className="flex-1">{it.produtoNome}</div>
                <div className="w-10 text-right">{it.quantidade}</div>
                <div className="w-10 text-center">{it.medSigla}</div>
                <div className="w-16 text-right">{formatCurrency(it.precoUnitario)}</div>
                <div className="w-16 text-right">{formatCurrency(it.valorTotal)}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Separador entre itens e totais */}
        <div className="w-full overflow-hidden whitespace-nowrap text-[11px] font-mono select-none my-0">{'='.repeat(256)}</div>

        <div className="mt-0 text-sm">
          <div className="flex justify-between">
            <span>Subtotal</span>
            <span>{formatCurrency(subtotal)}</span>
          </div>
          <div className="flex justify-between font-bold">
            <span>Total</span>
            <span>{formatCurrency(total)}</span>
          </div>
          <div className="flex justify-between">
            <span>Valor Pago</span>
            <span>{formatCurrency(totalPago)}</span>
          </div>
          <div className="flex justify-between">
            <span>Troco</span>
            <span>{formatCurrency(troco)}</span>
          </div>
        </div>

        {/* Separador entre totais e forma de pagamento */}
        <div className="w-full overflow-hidden whitespace-nowrap text-[11px] font-mono select-none my-0">{'='.repeat(256)}</div>

        <div className="mt-0 text-sm">
          <p className="font-bold">Forma de pagamento</p>
          {pagos.length === 0 ? (
            <p className="text-xs text-black">Nenhum pagamento efetuado</p>
          ) : (
            <div className="text-xs">
              {pagos.map((p) => (
                <div key={p.id} className="flex justify-between">
                  <span>{p.tipoPagamentoNome || p.tipoPagamentoId}</span>
                  <span>{formatCurrency(p.valor)}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="text-center mt-4 text-xs">
          <p>** Obrigado pela preferência **</p>
        </div>
      </div>

      <style>{`
        @media print {
          .print\\:hidden { display: none !important; }
          body { background: white; }
          .max-w-md { box-shadow: none !important; }
          /* Forçar texto 100% preto na impressão do cupom */
          .cupom-root, .cupom-root * { color: #000 !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        }
      `}</style>
    </div>
  );
};

export default CupomPrintPage;